import { useCustomerAnalytics } from "../../../../services/hooks/useCustomerAnalytics";
import { useLoyaltyUiModel } from "../../../../services/hooks/useLoyaltyUiModel";
import { useAppSelector } from "../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import { CUSTOMER_ANALYTICS_EVENT } from "../../../../utils/enums";
import {
  getLocalizedText,
  localizedNumber,
} from "../../../../utils/help/localization/localization";
import { LoyaltyTierSystemMilestone } from "../../../../utils/loyaltyTypes";
import { PointingMilestone } from "../../../../utils/types";
import { LoyaltyPointsIcon } from "../../../global/Icons/LoyaltyPointsIcon";
import { MileStone } from "../../../global/Icons/loyalty.icons";
import styles from "./LoyaltyPointsProgressBar.module.scss";

interface LoyaltyPointsProgressBarProps {
  onMilestoneBarClick?: () => void;
  tierColor?: string;
  /** Figma tiers meter: 8px bar, tier-coloured stars, points only (no names). */
  tierVariant?: boolean;
}

const toPointingMilestone = (
  m: LoyaltyTierSystemMilestone,
  index: number
): PointingMilestone => ({
  id: index,
  points: m.points,
  name: m.name.en ?? Object.values(m.name).find((v) => v) ?? "",
  nameLocalized: m.name.ar ?? m.name["ar-SA"] ?? m.name.fr,
});

export const LoyaltyPointsProgressBar = ({
  onMilestoneBarClick,
  tierColor,
  tierVariant = false,
}: LoyaltyPointsProgressBarProps) => {
  const addCustomerAnalytics = useCustomerAnalytics();
  const { t, i18n } = useTranslationI18();
  const pointsText = t("pointing-system:point");
  const {
    displayPoints: userPoints,
    currentTier,
    useLoyaltyService,
  } = useLoyaltyUiModel(pointsText, i18n);

  const pointingSystem = useAppSelector(
    (state) => state.cartAndLoyaltyReducer
  ).pointingSystem;

  let mileStones: PointingMilestone[];
  if (useLoyaltyService) {
    mileStones = (currentTier?.milestones ?? []).map(toPointingMilestone);
  } else {
    mileStones = [] as PointingMilestone[];
    for (const offer of pointingSystem?.restaurantPackageOffers ?? []) {
      offer.pointingMilestone &&
        !mileStones.find((m) => m.points === offer.pointingMilestone?.points) &&
        mileStones.push(offer.pointingMilestone);
    }
  }
  let maxPoints = Math.max(...mileStones.map((m) => m.points));
  if (userPoints > maxPoints) {
    if (userPoints / maxPoints <= 1.5) {
      maxPoints = userPoints;
    } else {
      maxPoints *= 1.5;
    }
  }

  const getPercentageStyle = (points: number): React.CSSProperties => {
    const progress = points > 0 ? scaleValue(points) * 100 : 0;
    const clampedProgress = Math.max(0, Math.min(100, progress)); // Clamp between 0 and 100

    return {
      "--progress": `${clampedProgress}%`,
    } as React.CSSProperties;
  };

  const scaleValue = (currentVal: number): number => {
    const updatedMileStones = [{ points: 0 }, ...mileStones];
    if (currentVal <= updatedMileStones[0].points) {
      return 0; // No progress if the value is below the first milestone
    }

    const lastMilestone =
      updatedMileStones[updatedMileStones.length - 1].points;
    if (currentVal >= lastMilestone) {
      return 1; // Full progress if the value is above the last milestone
    }

    // Find the two milestones that the currentVal is between
    let lowerIndex = 0;
    for (let i = 1; i < updatedMileStones.length; i++) {
      if (currentVal < updatedMileStones[i].points) {
        lowerIndex = i - 1;
        break;
      }
    }

    const lowerMilestone = updatedMileStones[lowerIndex].points;
    const upperMilestone = updatedMileStones[lowerIndex + 1].points;
    const segmentProgress =
      (currentVal - lowerMilestone) / (upperMilestone - lowerMilestone);
    const lowerBound = lowerIndex / (updatedMileStones.length - 1);
    const upperBound = (lowerIndex + 1) / (updatedMileStones.length - 1);
    const overallProgress =
      lowerBound + (upperBound - lowerBound) * segmentProgress;

    return overallProgress;
  };

  const handleMeterClick = () => {
    if (onMilestoneBarClick) {
      onMilestoneBarClick();

      return;
    }

    addCustomerAnalytics(
      CUSTOMER_ANALYTICS_EVENT.DEAD_CLICKS_ON_MILESTONE_METER
    );
  };

  return (
    <div
      className={`${styles.progress_container} ${
        tierVariant ? styles.tier_variant : ""
      }`}
      style={
        tierColor
          ? ({ "--loyalty-meter-color": tierColor } as React.CSSProperties)
          : undefined
      }
    >
      <div
        className={`${styles.progress_bar_container} ${
          onMilestoneBarClick ? styles.clickable : ""
        }`}
        onClick={handleMeterClick}
        data-testid="milestone-bar"
      >
        <div className={`${styles.progress_bar} `}>
          <div
            className={styles.inner_progress_bar}
            style={getPercentageStyle(
              userPoints <= maxPoints ? userPoints : maxPoints
            )}
            data-testid="progress-bar"
          ></div>
          {mileStones.map((milestone, index) => (
            <div
              className={`${styles.offer_container} ${
                milestone.points > userPoints
                  ? styles.coming_milestone
                  : styles.reached_milestone
              }`}
              data-testid={
                milestone.points > userPoints
                  ? `grey-milestone-${index}`
                  : `yellow-milestone-${index}`
              }
              style={getPercentageStyle(milestone.points)}
              key={index}
            >
              <div className={styles.milestone_icon}>
                {tierVariant ? <LoyaltyPointsIcon /> : <MileStone />}
              </div>
              <div
                className={styles.milestone_content}
                data-testid={`milestone-content-${index}`}
              >
                {!tierVariant && milestone.name && (
                  <span className={styles.milestone_name}>
                    {getLocalizedText(milestone.name, milestone.nameLocalized)}
                  </span>
                )}
                <span className={styles.milestone_points}>
                  {localizedNumber(milestone.points)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
